import React, { useState } from "react";
import { LuPlus, LuMinus } from "react-icons/lu";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
  {
    question: "What is PayUp and how does it work?",
    answer: "PayUp is a modern payment platform that allows businesses to accept, send, and manage payments globally. It connects your systems, processes transactions securely, and provides real-time insights through a unified dashboard."
  },
  {
    question: "How quickly can I get started?",
    answer: "You can get started in minutes. Simply sign up for an account, complete our fast verification process, and you'll be ready to process your first transaction."
  },
  {
    question: "Which countries and currencies does PayUp support?",
    answer: "We support over 150 countries and 50+ different currencies, allowing you to operate truly globally."
  },
  {
    question: "Is PayUp secure?",
    answer: "Yes, security is our top priority. We use bank-grade encryption, are PCI-DSS Level 1 compliant, and employ advanced fraud prevention systems."
  },
  {
    question: "What payment methods are supported?",
    answer: "We support all major credit and debit cards, popular digital wallets like Apple Pay and Google Pay, as well as local payment methods in various regions."
  },
  {
    question: "Are there any hidden fees?",
    answer: "No, we believe in complete transparency. Our pricing structure is simple and straightforward with no hidden charges."
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="w-full bg-white py-24 md:py-32 px-4 md:px-8 relative overflow-hidden">
      <link href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;700&display=swap" rel="stylesheet" />
      <div className="max-w-[760px] mx-auto relative z-10 w-full">
        <div className="absolute -right-[180px] top-[40px] z-0 hidden lg:block opacity-90 pointer-events-none">
          <img
            src="https://cdn.jiro.build/Random/dolar%20note.png"
            alt="Dollar Roll"
            className="w-[200px] rotate-[35deg] drop-shadow-xl"
          />
        </div>

        <div className="text-center mb-14 relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="text-[56px] md:text-[72px] leading-[1.1] md:leading-[80px] font-['Anton',sans-serif] font-normal text-black uppercase mb-4 tracking-normal"
          >
            Frequently Asked Questions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-[17px] md:text-[18px] text-gray-500 max-w-[480px] mx-auto leading-relaxed"
          >
            Everything you need to know about PayUp. Can't find your answer? Contact our support team.
          </motion.p>
        </div>

        <div className="space-y-4 relative z-10">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.05 * index, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-[16px] overflow-hidden transition-colors bg-[#F6F7F6]"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-6 flex items-center justify-between text-left"
              >
                <span className="text-[18px] md:text-[20px] font-medium text-[#111]">
                  {faq.question}
                </span>
                <span className="flex-shrink-0 ml-4">
                  {openIndex === index ? (
                    <LuMinus className="w-6 h-6 text-[#111]" strokeWidth={1.5} />
                  ) : (
                    <LuPlus className="w-6 h-6 text-[#111]" strokeWidth={1.5} />
                  )}
                </span>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <div className="px-6 pb-7 pt-2 text-gray-500 text-[16px] md:text-[17px] leading-[1.6]">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}